<template>
  <el-dialog
      title="统一配置数据源"
      :visible.sync="dialogVisible"
      width="600px"
      :close-on-click-modal="false"
      append-to-body
      @closed="handleClosed"
  >
    <el-form :model="form" :rules="rules" ref="batchForm" label-width="100px" size="small">
      <el-form-item label="数据源" prop="dataSourceId">
        <div style="display:flex;gap:8px;width:100%;">
          <el-select v-model="form.dataSourceId" placeholder="请选择数据源" style="flex:1;" filterable
                     @change="onSourceChange">
            <el-option
                v-for="item in sourceOptions"
                :key="item.id"
                :label="item.name"
                :value="item.id">
            </el-option>
          </el-select>
          <el-button type="primary" plain size="small" @click="$emit('go-source')">
            <i class="el-icon-link"></i> 管理
          </el-button>
        </div>
      </el-form-item>

      <el-form-item label="选择模块" prop="moduleNames">
        <el-select v-model="form.moduleNames" placeholder="请选择要配置的模块" style="width:100%;" multiple filterable>
          <el-option
              v-for="m in modules"
              :key="m.id"
              :label="m.name"
              :value="m.name">
          </el-option>
        </el-select>
      </el-form-item>

      <el-form-item label="数据库名">
        <el-input v-model="form.name" placeholder="将作为各模块的数据库名后缀，如：_db">
          <template v-slot:prepend>模块名 + </template>
        </el-input>
      </el-form-item>

      <el-form-item label="作者名" prop="authorName">
        <el-input v-model="form.authorName" placeholder="请输入作者名"></el-input>
      </el-form-item>

      <el-form-item label="包名" prop="packageName">
        <el-input v-model="form.packageName" placeholder="com.yida">
          <template v-slot:prepend>包名前缀</template>
        </el-input>
      </el-form-item>

      <el-form-item label="表前缀">
        <el-input v-model="form.prefix" placeholder="例如：t_（为空则不处理）"></el-input>
      </el-form-item>

      <el-form-item label="模块描述" prop="moduleDescription">
        <el-input v-model="form.moduleDescription" type="textarea" :rows="2" placeholder="各模块共用此描述"></el-input>
      </el-form-item>

      <el-form-item label="备注" prop="comment">
        <el-input v-model="form.comment" placeholder="请输入备注"></el-input>
      </el-form-item>

      <el-collapse style="border:none;margin-top:-8px;">
        <el-collapse-item title="高级路径配置">
          <el-form-item label="后端路径">
            <el-input v-model="form.javaPath" placeholder="例如：/src/main/java"></el-input>
          </el-form-item>
          <el-form-item label="前端路径">
            <el-input v-model="form.vuePath" placeholder="例如：/src/views"></el-input>
          </el-form-item>
        </el-collapse-item>
      </el-collapse>
    </el-form>

    <span slot="footer" class="dialog-footer">
      <el-button @click="$emit('update:visible', false)" size="small">取 消</el-button>
      <el-button type="primary" @click="handleSubmit" :loading="submitting" size="small">
        <i class="el-icon-check"></i> 批量配置 ({{ form.moduleNames.length }} 个模块)
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
/**
 * 项目级批量数据源配置
 * @author PeterLee
 * @since 2025-05-11
 */
import { addData, getList } from "@/api/baseApi";
import { saveModuleConfig } from "@/api/dataApi";

export default {
  name: 'BatchSourceConfig',
  props: {
    visible: Boolean,
    modules: { type: Array, default: () => [] },
    sourceOptions: { type: Array, default: () => [] }
  },
  data() {
    return {
      submitting: false,
      form: {
        dataSourceId: null,
        moduleNames: [],
        name: '',
        authorName: 'Auto Generated',
        packageName: 'com.yida',
        prefix: '',
        moduleDescription: '',
        comment: '',
        javaPath: '',
        vuePath: ''
      },
      rules: {
        dataSourceId: [{ required: true, message: '请选择数据源', trigger: 'change' }],
        moduleNames: [{ type: 'array', required: true, message: '请至少选择一个模块', trigger: 'change' }]
      }
    }
  },
  computed: {
    dialogVisible: {
      get() { return this.visible },
      set(val) { this.$emit('update:visible', val) }
    }
  },
  methods: {
    onSourceChange() {},
    resetForm() {
      this.form = {
        dataSourceId: null,
        moduleNames: [],
        name: '',
        authorName: 'Auto Generated',
        packageName: 'com.yida',
        prefix: '',
        moduleDescription: '',
        comment: '',
        javaPath: '',
        vuePath: ''
      };
      this.$nextTick(() => {
        if (this.$refs.batchForm) {
          this.$refs.batchForm.clearValidate();
        }
      });
    },
    async handleSubmit() {
      try {
        await this.$refs.batchForm.validate();
      } catch {
        return;
      }
      this.submitting = true;
      let successCount = 0;
      try {
        // Phase 1: 创建数据库连接记录
        for (const moduleName of this.form.moduleNames) {
          const dbName = moduleName + (this.form.name || '_db');
          await addData('data', 'dataBase', {
            dataSourceId: this.form.dataSourceId,
            name: dbName,
            comment: this.form.comment || moduleName + ' 数据库',
            state: 1
          });
        }

        // Phase 2: 查询数据库记录获取 ID
        const listRes = await getList('data', 'dataBase');
        const dbs = (listRes.data || []).filter(
          d => d.dataSourceId === this.form.dataSourceId
        );

        // Phase 3: 逐个模块保存配置
        for (const moduleName of this.form.moduleNames) {
          const module = this.modules.find(m => m.name === moduleName);
          if (!module) {
            console.warn('模块 ' + moduleName + ' 未找到，跳过');
            continue;
          }

          const dbName = moduleName + (this.form.name || '_db');
          const db = dbs.find(d => d.name === dbName);
          if (!db) {
            console.warn('数据库记录 ' + dbName + ' 未找到，跳过');
            continue;
          }

          const res = await saveModuleConfig(module.id, db.id, {
            authorName: this.form.authorName,
            packageName: this.form.packageName + '.' + moduleName.toLowerCase().replace(/-/g, '.'),
            prefix: this.form.prefix,
            moduleDescription: this.form.moduleDescription || moduleName + ' 模块',
            javaPath: this.form.javaPath,
            vuePath: this.form.vuePath
          });
          if (res.code === 200) successCount++;
        }

        this.$message.success(`成功配置 ${successCount} / ${this.form.moduleNames.length} 个模块`);
        this.$emit('update:visible', false);
        this.$emit('done');
      } catch (error) {
        console.error('批量配置失败:', error);
        this.$message.error('批量配置失败');
      } finally {
        this.submitting = false;
      }
    },
    handleClosed() {
      this.submitting = false;
      this.resetForm();
    }
  }
}
</script>
